import { Col, Container, Row, Space, useMediaQuery } from '@ag1/ui';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { HeroHalfV5 as HeroHalfV5Props, SanityKeyed } from 'types/studio/studio';
import { CommonSectionProps } from '../types';
import styles from './HeroHalfV5.module.scss';
import HeroHalfV5Content from './HeroHalfV5Content/HeroHalfV5Content';
import HeroHalfV5Media from './HeroHalfV5Media/HeroHalfV5Media';

type Props = SanityKeyed<HeroHalfV5Props> &
  CommonSectionProps & {
    className?: string;
  };

const HeroHalfV5 = ({
  _key: key,
  _type: sectionType,
  className,
  textAlignment,
  headline,
  superHeadline,
  superHeadlineVariation,
  subtitle,
  logo,
  logoSize,
  logoPosition,
  bodyText,
  caption,
  captionIcon,
  captionIconMobile,
  unifiedButton,
  hasButtonOnMobile,
  unifiedButtonMobile,
  additionalLinkSwitch,
  additionalLink,
  hasAdditionalLinkOnMobile,
  additionalLinkMobile,
  additionalTarget,
  hoverColor,
  media,
  isFullScreenBg,
  isDarkMode: isDarkModeFullScreenBg,
  mediaSizeNarrow,
  ratingValue,
  ratingTitle,
  ratingMobilePosition,
  ratingStarColor,
  ratingStarColorMobile,
  benefitsList,
  benefitsAlignToStart,
  contentPosition = 'left',
  padding,
  hugPadding = 'medium',
  background,
  footnote,
  showfootnote,
  rtbTagsTextColor,
  rtbTagsTextColorMobile,
  rtbTags,
  showTruemedEligibility,
  visibleOnPageLoad
}: Props) => {
  const [mounted, setMounted] = useState(false);
  const aboveMedium = useMediaQuery('m');
  const paddingClasses = clsx(getSectionPaddingClasses(styles, padding));
  const theme = 'new';

  useEffect(() => {
    setMounted(true);
  }, []);

  const shouldShowFullScreenBg = useMemo(
    () => (mounted ? aboveMedium : true) && isFullScreenBg,
    [mounted, aboveMedium, isFullScreenBg]
  );

  // Make text-color of components change to a lighter color when background is darker (dark mode)
  const isDarkMode = useMemo(
    () =>
      ((mounted ? aboveMedium : true) && isDarkModeFullScreenBg) ||
      (!shouldShowFullScreenBg && background === 'green'),
    [mounted, aboveMedium, isDarkModeFullScreenBg, shouldShowFullScreenBg, background]
  );

  return (
    <div
      className={clsx(
        styles[`bg-${background}`],
        getThemeCSSClass(theme),
        paddingClasses,
        className
      )}>
      {!mediaSizeNarrow && shouldShowFullScreenBg && (
        <div className={styles.rootFullScreenBg}>
          <div className={styles.fullScreenBg}>
            <HeroHalfV5Media
              media={media}
              contentPosition={contentPosition}
              isFullScreenBg={isFullScreenBg}
              mediaSizeNarrow={mediaSizeNarrow}
              imageSizes={{ xxs: 12, m: 6 }}
              eagerLoading={visibleOnPageLoad}
            />
          </div>
          <Container narrow id={key} className={styles.containerFullScreenBg}>
            <div
              className={clsx(
                styles.contentFullScreenBg,
                contentPosition === 'right' && styles.alignToRight,
                contentPosition === 'center' && styles.alignToCenter
              )}>
              <HeroHalfV5Content
                sectionType={sectionType}
                mediaSizeNarrow={mediaSizeNarrow}
                textAlignment={textAlignment}
                headline={headline}
                superHeadline={superHeadline}
                superHeadlineVariation={superHeadlineVariation}
                logo={logo}
                bodyText={bodyText}
                caption={caption}
                captionIcon={captionIcon}
                captionIconMobile={captionIconMobile}
                unifiedButton={unifiedButton}
                additionalLinkSwitch={additionalLinkSwitch}
                additionalLink={additionalLink}
                hasAdditionalLinkOnMobile={hasAdditionalLinkOnMobile}
                additionalLinkMobile={additionalLinkMobile}
                additionalTarget={additionalTarget}
                hoverColor={hoverColor}
                subtitle={subtitle}
                contentPosition={contentPosition}
                isFullScreenBg={isFullScreenBg}
                ratingValue={ratingValue}
                ratingTitle={ratingTitle}
                ratingMobilePosition={ratingMobilePosition}
                ratingStarColor={ratingStarColor}
                ratingStarColorMobile={ratingStarColorMobile}
                benefitsList={benefitsList}
                benefitsAlignToStart={benefitsAlignToStart}
                theme={theme}
                showTruemedEligibility={showTruemedEligibility}
                footnote={footnote}
                showfootnote={showfootnote}
                rtbTagsTextColor={rtbTagsTextColor}
                rtbTagsTextColorMobile={rtbTagsTextColorMobile}
                rtbTags={rtbTags}
                hugPadding={hugPadding}
                visibleOnPageLoad={visibleOnPageLoad}
                isDarkMode={isDarkMode}
              />
            </div>
          </Container>
        </div>
      )}
      {!mediaSizeNarrow && !shouldShowFullScreenBg && (
        <div className={clsx(styles.root, contentPosition === 'left' && styles.reverse)} id={key}>
          <Col className={styles.noPadding} span={{ xxs: 12, m: 6 }}>
            <HeroHalfV5Media
              media={media}
              contentPosition={contentPosition}
              isFullScreenBg={isFullScreenBg}
              mediaSizeNarrow={mediaSizeNarrow}
              imageSizes={{ xxs: 12, m: 6 }}
              eagerLoading={visibleOnPageLoad}
            />
          </Col>
          <Col
            className={clsx(styles.colContent, styles.noPadding)}
            span={{ xxs: 12, m: 6, xl: 5 }}
            offset={{ xl: contentPosition === 'left' ? 0 : 1 }}>
            <Container
              className={contentPosition === 'left' ? styles.clearRight : styles.clearLeft}>
              <HeroHalfV5Content
                sectionType={sectionType}
                mediaSizeNarrow={mediaSizeNarrow}
                textAlignment={textAlignment}
                headline={headline}
                superHeadline={superHeadline}
                superHeadlineVariation={superHeadlineVariation}
                subtitle={subtitle}
                logo={logo}
                logoSize={logoSize}
                logoPosition={logoPosition}
                bodyText={bodyText}
                unifiedButton={unifiedButton}
                hasButtonOnMobile={hasButtonOnMobile}
                unifiedButtonMobile={unifiedButtonMobile}
                additionalLinkSwitch={additionalLinkSwitch}
                additionalLink={additionalLink}
                hasAdditionalLinkOnMobile={hasAdditionalLinkOnMobile}
                additionalLinkMobile={additionalLinkMobile}
                additionalTarget={additionalTarget}
                hoverColor={hoverColor}
                caption={caption}
                captionIcon={captionIcon}
                captionIconMobile={captionIconMobile}
                contentPosition={contentPosition}
                isFullScreenBg={isFullScreenBg}
                ratingValue={ratingValue}
                ratingTitle={ratingTitle}
                ratingMobilePosition={ratingMobilePosition}
                ratingStarColor={ratingStarColor}
                ratingStarColorMobile={ratingStarColorMobile}
                benefitsList={benefitsList}
                benefitsAlignToStart={benefitsAlignToStart}
                theme={theme}
                showTruemedEligibility={showTruemedEligibility}
                footnote={footnote}
                showfootnote={showfootnote}
                rtbTagsTextColor={rtbTagsTextColor}
                rtbTagsTextColorMobile={rtbTagsTextColorMobile}
                rtbTags={rtbTags}
                hugPadding={hugPadding}
                visibleOnPageLoad={visibleOnPageLoad}
                isDarkMode={isDarkMode}
              />
            </Container>
          </Col>
        </div>
      )}
      {mediaSizeNarrow && !shouldShowFullScreenBg && (
        <Container narrow id={key}>
          <Space
            top={{ xxs: 40, s: 60, m: 80, l: 100 }}
            bottom={{ m: 80, l: 100 }}
            className={paddingClasses}>
            <Row
              align={{ l: 'between' }}
              className={clsx(styles.root, contentPosition === 'left' && styles.reverse)}>
              <Col span={{ xxs: 12, m: 6 }}>
                <HeroHalfV5Media
                  media={media}
                  contentPosition={contentPosition}
                  isFullScreenBg={isFullScreenBg}
                  mediaSizeNarrow={mediaSizeNarrow}
                  imageSizes={{ xxs: 12, m: 6 }}
                  eagerLoading={visibleOnPageLoad}
                />
              </Col>
              <Col
                className={styles.colContent}
                span={{ xxs: 12, m: 6, l: { value: 5, wide: true }, xl: 5 }}>
                <HeroHalfV5Content
                  sectionType={sectionType}
                  mediaSizeNarrow={mediaSizeNarrow}
                  textAlignment={textAlignment}
                  headline={headline}
                  superHeadline={superHeadline}
                  superHeadlineVariation={superHeadlineVariation}
                  logo={logo}
                  bodyText={bodyText}
                  caption={caption}
                  captionIcon={captionIcon}
                  captionIconMobile={captionIconMobile}
                  unifiedButton={unifiedButton}
                  hasButtonOnMobile={hasButtonOnMobile}
                  unifiedButtonMobile={unifiedButtonMobile}
                  additionalLinkSwitch={additionalLinkSwitch}
                  additionalLink={additionalLink}
                  hasAdditionalLinkOnMobile={hasAdditionalLinkOnMobile}
                  additionalLinkMobile={additionalLinkMobile}
                  additionalTarget={additionalTarget}
                  hoverColor={hoverColor}
                  subtitle={subtitle}
                  contentPosition={contentPosition}
                  isFullScreenBg={isFullScreenBg}
                  ratingValue={ratingValue}
                  ratingTitle={ratingTitle}
                  ratingMobilePosition={ratingMobilePosition}
                  ratingStarColor={ratingStarColor}
                  ratingStarColorMobile={ratingStarColorMobile}
                  benefitsList={benefitsList}
                  benefitsAlignToStart={benefitsAlignToStart}
                  theme={theme}
                  showTruemedEligibility={showTruemedEligibility}
                  footnote={footnote}
                  showfootnote={showfootnote}
                  rtbTagsTextColor={rtbTagsTextColor}
                  rtbTagsTextColorMobile={rtbTagsTextColorMobile}
                  rtbTags={rtbTags}
                  hugPadding={hugPadding}
                  visibleOnPageLoad={visibleOnPageLoad}
                  isDarkMode={isDarkMode}
                />
              </Col>
            </Row>
          </Space>
        </Container>
      )}
    </div>
  );
};

export default HeroHalfV5;
